import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getDashboardStats, getLeads, getDashboardTasks, getHotLeads, DashboardTask, HotLead } from '../lib/api';
import { Users, UserCheck, TrendingUp, Phone, ArrowLeft, Clock, AlertTriangle, Calendar, Phone as PhoneIcon, MessageCircle, MessageSquare, Flame, Thermometer, Snowflake } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';

const PRIORITY_STYLES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const CHANNEL_ICON: Record<string, React.ReactNode> = {
  call: <PhoneIcon className="w-3 h-3" />,
  whatsapp: <MessageCircle className="w-3 h-3" />,
  sms: <MessageSquare className="w-3 h-3" />,
};

const CHANNEL_STYLES: Record<string, string> = {
  call: 'bg-blue-100 text-blue-700',
  whatsapp: 'bg-green-100 text-green-700',
  sms: 'bg-purple-100 text-purple-700',
};

// Score label styles and icons
const SCORE_LABEL_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  hot: { bg: 'bg-red-100', text: 'text-red-700', icon: <Flame className="w-4 h-4 text-red-500" /> },
  warm: { bg: 'bg-orange-100', text: 'text-orange-700', icon: <Thermometer className="w-4 h-4 text-orange-500" /> },
  cold: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <Snowflake className="w-4 h-4 text-blue-500" /> },
};

function HotLeadRow({ lead, t }: { lead: HotLead; t: (key: string) => string }) {
  const style = SCORE_LABEL_STYLES[lead.score_label] || SCORE_LABEL_STYLES.cold;
  
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition border border-gray-100"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${style.bg} flex items-center justify-center`}>
          {style.icon}
        </div>
        <div>
          <p className="font-medium text-gray-900">
            {lead.first_name || lead.last_name 
              ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
              : lead.phone}
          </p>
          <p className="text-sm text-gray-500" dir="ltr">{lead.phone}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-left">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${style.bg} ${style.text}`}>
            {style.icon}
            {lead.score}
          </span>
        </div>
        <span className={`badge badge-${lead.status}`}>
          {t(`stage.${lead.status}`)}
        </span>
      </div>
    </Link>
  );
}

function TaskRow({ task, t, formatDate }: { task: DashboardTask; t: (key: string) => string; formatDate: (date: Date, format: 'date' | 'time' | 'datetime') => string }) {
  const dueDate = new Date(task.due_at.replace(' ', 'T'));
  const timeStr = formatDate(dueDate, 'time');
  
  return (
    <Link 
      to={`/leads/${task.lead_id}?tab=tasks`}
      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition border border-gray-100"
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{task.title}</p>
        <p className="text-sm text-gray-500 truncate">
          {task.lead_name || task.lead_phone || `${t('nav.leads')} #${task.lead_id}`}
        </p>
      </div>
      <div className="flex items-center gap-2 ms-3">
        <span className="text-xs text-gray-500">{timeStr}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${CHANNEL_STYLES[task.channel]}`}>
          {CHANNEL_ICON[task.channel]}
        </span>
        <span className={`px-2 py-0.5 rounded text-xs ${PRIORITY_STYLES[task.priority]}`}>
          {t(`task.priority.${task.priority}`)}
        </span>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [activeTaskTab, setActiveTaskTab] = useState<'overdue' | 'today'>('overdue');
  const { t, formatDate } = useLanguage();

  const { data: statsData, isLoading: _loadingStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: getDashboardStats,
  });

  const { data: recentLeads, isLoading: loadingLeads } = useQuery({
    queryKey: ['recent-leads'],
    queryFn: () => getLeads({ limit: 5 }),
  });

  const { data: tasksData, isLoading: loadingTasks } = useQuery({
    queryKey: ['dashboard-tasks'],
    queryFn: () => getDashboardTasks({ limit: 5 }),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: hotLeadsData, isLoading: loadingHotLeads, isError: hotLeadsError, refetch: refetchHotLeads } = useQuery({
    queryKey: ['hot-leads'],
    queryFn: () => getHotLeads({ limit: 10, min_score: 0 }),
    refetchInterval: 120000, // Refresh every 2 minutes
  });

  const stats = statsData?.data || {};
  
  // Safe extraction helper - handles any response shape
  const extractArray = <T = any>(x: any): T[] => {
    if (Array.isArray(x)) return x;
    if (Array.isArray(x?.leads)) return x.leads;
    if (Array.isArray(x?.data?.leads)) return x.data.leads;
    if (Array.isArray(x?.data)) return x.data;
    if (Array.isArray(x?.items)) return x.items;
    return [];
  };
  
  // Safe extraction of recent leads - API returns { leads: [], pagination: {} }
  const leads = extractArray(recentLeads?.data);
  
  // Safe extraction of tasks data - handle various response shapes
  const rawTasks = (tasksData?.data?.data ?? tasksData?.data ?? {}) as {
    overdue_count?: number;
    today_count?: number;
    upcoming_count?: number;
    overdue?: DashboardTask[];
    today?: DashboardTask[];
  };
  const tasks = {
    overdue_count: rawTasks?.overdue_count ?? 0,
    today_count: rawTasks?.today_count ?? 0,
    upcoming_count: rawTasks?.upcoming_count ?? 0,
    overdue: Array.isArray(rawTasks?.overdue) ? rawTasks.overdue : [] as DashboardTask[],
    today: Array.isArray(rawTasks?.today) ? rawTasks.today : [] as DashboardTask[],
  };
  
  // Safe extraction of hot leads - MUST be array, never crash on .map()
  const hotLeads: HotLead[] = extractArray(hotLeadsData?.data) as HotLead[];

  const statCards = [
    {
      label: t('dashboard.total_leads'),
      value: stats.total_leads || 0,
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      label: t('dashboard.new_leads'),
      value: stats.new_leads || 0,
      icon: UserCheck,
      color: 'bg-green-500',
    },
    {
      label: t('dashboard.conversion_rate'),
      value: `${stats.conversion_rate || 0}%`,
      icon: TrendingUp,
      color: 'bg-purple-500',
    },
    {
      label: t('dashboard.calls_today'),
      value: stats.calls_today || 0,
      icon: Phone,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('nav.dashboard')}</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, idx) => (
          <div key={idx} className="card">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tasks Widget */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            {t('dashboard.tasks')}
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="w-4 h-4" />
                {t('dashboard.overdue')}: {tasks.overdue_count}
              </span>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1 text-blue-600">
                <Calendar className="w-4 h-4" />
                {t('dashboard.today')}: {tasks.today_count}
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-gray-500">
                {t('dashboard.upcoming')}: {tasks.upcoming_count}
              </span>
            </div>
          </div>
        </div>

        {/* Task Tabs */}
        <div className="flex gap-2 mb-4 border-b">
          <button
            onClick={() => setActiveTaskTab('overdue')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTaskTab === 'overdue'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('dashboard.overdue')} ({tasks.overdue_count})
          </button>
          <button
            onClick={() => setActiveTaskTab('today')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTaskTab === 'today'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('dashboard.today')} ({tasks.today_count})
          </button>
        </div>

        {/* Task List */}
        {loadingTasks ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {activeTaskTab === 'overdue' ? (
              tasks.overdue.length === 0 ? (
                <p className="text-gray-500 text-center py-6">{t('dashboard.no_overdue_tasks')}</p>
              ) : (
                tasks.overdue.map((task) => <TaskRow key={task.id} task={task} t={t} formatDate={formatDate} />)
              )
            ) : (
              tasks.today.length === 0 ? (
                <p className="text-gray-500 text-center py-6">{t('dashboard.no_today_tasks')}</p>
              ) : (
                tasks.today.map((task) => <TaskRow key={task.id} task={task} t={t} formatDate={formatDate} />)
              )
            )}
          </div>
        )}
      </div>

      {/* Hot Leads Widget */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Flame className="w-5 h-5 text-red-500" />
            {t('dashboard.hot_leads')}
          </h2>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
              Hot ≥60
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-orange-500"></span>
              Warm ≥30
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
              Cold &lt;30
            </span>
          </div>
        </div>

        {loadingHotLeads ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        ) : hotLeadsError ? (
          <div className="text-center py-6">
            <p className="text-red-500 mb-2">{t('dashboard.hot_leads_error')}</p>
            <button 
              onClick={() => refetchHotLeads()} 
              className="text-primary-600 hover:underline text-sm"
            >
              {t('action.retry')}
            </button>
          </div>
        ) : hotLeads.length === 0 ? (
          <p className="text-gray-500 text-center py-6">{t('dashboard.no_hot_leads')}</p>
        ) : (
          <div className="space-y-2">
            {hotLeads.slice(0, 10).map((lead) => (
              <HotLeadRow key={lead.id} lead={lead} t={t} />
            ))}
          </div>
        )}
      </div>

      {/* Pipeline Overview */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold mb-4">{t('dashboard.sales_pipeline')}</h2>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['new', 'contacted', 'qualified', 'won', 'lost'].map((stage) => {
            const count = stats[`${stage}_count`] || 0;
            return (
              <Link
                key={stage}
                to={`/leads?stage=${stage}`}
                className={`flex-1 min-w-[120px] p-4 rounded-lg text-center transition hover:opacity-80 badge-${stage}`}
              >
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm">{t(`stage.${stage}`)}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('dashboard.recent_leads')}</h2>
          <Link to="/leads" className="text-primary-600 hover:underline flex items-center gap-1 text-sm">
            {t('action.view_all')}
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </div>

        {loadingLeads ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('leads.no_leads')}</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('form.name')}</th>
                  <th>{t('form.phone')}</th>
                  <th>{t('lead.source')}</th>
                  <th>{t('lead.stage')}</th>
                  <th>{t('form.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map((lead: any) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td>
                      <Link to={`/leads/${lead.id}`} className="font-medium text-primary-600 hover:underline">
                        {lead.name}
                      </Link>
                    </td>
                    <td dir="ltr" className="text-gray-600">{lead.phone}</td>
                    <td>{lead.source || '-'}</td>
                    <td>
                      <span className={`badge badge-${lead.stage}`}>
                        {t(`stage.${lead.stage}`)}
                      </span>
                    </td>
                    <td className="text-gray-500">
                      {formatDate(new Date(lead.created_at), 'date')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
